import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import classNames from 'classnames'
import TitleRow from '../../../components/title-row'
import InfoInput from '../../../components/info-input'
import { Select } from '../../../components/UI'
import { FORM_FIELD_TYPES } from '../config'
import styles from '../styles.module.scss'

const FormField = ({
  field,
  value,
  error,
  onChange,
  onSelectChange,
  options = [],
  userData,
  ...props
}) => {
  const intl = useIntl()

  const fieldProps = useMemo(() => {
    const getPlaceholder = () => {
      if (!field.placeholder) return ''

      if (field.placeholder === 'userData?.email') return userData?.email || ''

      if (field.placeholder === 'userData?.url') return userData?.url || ''

      if (
        typeof field.placeholder === 'string' &&
        (field.placeholder.startsWith('global.') ||
          field.placeholder.startsWith('settings.') ||
          field.placeholder.startsWith('teams.'))
      ) {
        return intl.formatMessage({ id: field.placeholder })
      }

      return field.placeholder
    }

    const baseProps = {
      name: field.name,
      value: value || '',
      error: error || '',
      onChange: onChange,
      icon: field.icon,
      placeholder: getPlaceholder(),
      required: field.required,
    }

    // Filter out props that shouldn't be passed to InfoInput
    const filteredProps = Object.keys(props).reduce((acc, key) => {
      if (
        ![
          'onLanguageChange',
          'onSelectChange',
          'optionsMap',
          'userData',
        ].includes(key)
      ) {
        acc[key] = props[key]
      }
      return acc
    }, {})

    // Add specific props based on field type
    if (field.borderBottom) {
      baseProps.borderBottom = true
    }
    if (field.disabled) {
      baseProps.disabled = true
    }
    if (field.margin) {
      baseProps.margin = field.margin
    }
    if (field.type === FORM_FIELD_TYPES.DATE) {
      baseProps.type = 'date'
      baseProps.min = field.min
      baseProps.max = field.max
    }
    if (field.type === FORM_FIELD_TYPES.TEXTAREA) {
      baseProps.type = 'text'
      baseProps.multiline = 'true'
    }

    return { ...baseProps, ...filteredProps }
  }, [field, value, error, onChange, userData, intl, props])

  const titleRowProps = {
    className: classNames('settingsTitleBg'),
    title: <FormattedMessage id={field.label} />,
    color: 'white',
    padding: field.titlePadding || '9px 38px 7px',
    boxshadow: true,
    ...(field.required && { required: true }),
    ...(field.fontSize && { fontSize: field.fontSize }),
    ...(field.margin && { margin: field.margin }),
  }

  const renderField = () => {
    switch (field.type) {
      case FORM_FIELD_TYPES.SELECT:
        return (
          <Select
            name={field.name}
            clearable={field.clearable}
            values={value || []}
            options={options}
            onChange={
              field.onChange
                ? props[field.onChange]
                : values => onSelectChange(values, field.name)
            }
            type='text'
            icon={field.icon}
            placeholder={field.placeholder}
            error={error}
            dropdownPosition={field.dropdownPosition}
          />
        )

      case FORM_FIELD_TYPES.INPUT:
      case FORM_FIELD_TYPES.DATE:
      case FORM_FIELD_TYPES.TEXTAREA:
      default:
        return <InfoInput {...fieldProps} />
    }
  }

  return (
    <div className={classNames(styles.formField, { 
      [styles.error]: error, 
      [styles.success]: value && !error 
    })}>
      {field.label && <TitleRow {...titleRowProps} />}
      {renderField()}
    </div>
  )
}

export default FormField
